/**
 * RL-8 — Dimension-based intention lookup
 *
 * 입력 dimension partial spec → INTENTION_DIMENSIONS 65 entry 중 best match intentionKey 반환.
 *
 * 본질 — B4-1 modifier dropping 95% 구조적 차단:
 *   - LLM/RL-1이 6-axis dimensions (category/action/subject/modifier/aggregation/period) 채움
 *   - intention key 65개 문자열 외울 부담 → 차원 enum 채우기로 대체
 *   - modifier subset 검사로 "할인 회원 재등록률" 요청 시 modifier=[]인 RE_REGISTRATION_RATE 후보 자동 제외
 *     → DISCOUNT modifier preserve된 DISCOUNT_RETENTION_STATISTICS만 살아남음
 *
 * 매칭 알고리즘:
 *   1. category 불일치 → 후보 X (필수)
 *   2. action 불일치 → 후보 X (필수)
 *   3. modifier preservation (★ B4-1 핵심)
 *      - user modifier ⊆ catalog modifier (strict subset)
 *      - user가 'DISCOUNT' 요청 → catalog가 'DISCOUNT' 포함하지 않으면 후보 X
 *      - user modifier 빈 set → 모든 catalog modifier 허용 (over-spec 감점은 아래 4번)
 *   4. score 계산 (시작 100):
 *      - subject 지정 + 일치: +0 / 지정 + 불일치: -20 / 미지정: 0
 *      - aggregation 지정 + 일치: +0 / 지정 + 불일치: -15 / 미지정: 0
 *      - period 지정 + 일치: +0 / 지정 + 불일치: -15 / 미지정: 0
 *      - over-modifier (catalog가 user보다 많은 modifier 보유): -5 per extra
 *   5. score desc → top 1 (tie 시 첫 발견)
 *
 * Invalid dimension 처리:
 *   - enum 외 값 (예: category='UNKNOWN') 입력 시 throw 아님 → silent miss (단일 후보 0건 반환)
 *   - 차후 TL-7 ZWSP-strip / case-normalize 도입 시 보강
 *
 * Public API:
 *   findIntentionByDimensions(spec) → {key, dims, score} | null
 *   findIntentionsByDimensions(spec, {topK=5}) → Array<{key, dims, score}>
 *   resolveFromForceDimensions(forceDimensions) → {key, dims, score} | null
 *   DIMENSION_REQUIRED / DIMENSION_OPTIONAL (frozen)
 *
 * @typedef {{category: string, action: string, subject?: string,
 *            modifier?: string[], aggregation?: string, period?: string}} DimensionSpec
 *
 * @typedef {{key: string, dims: object, score: number}} LookupResult
 */

import { INTENTION_DIMENSIONS } from '../../AiManager/Transaction/aiPrompts.js';

export const DIMENSION_REQUIRED = Object.freeze(['category', 'action']);
export const DIMENSION_OPTIONAL = Object.freeze(['subject', 'modifier', 'aggregation', 'period']);

const SCORE_BASE = 100;
const PENALTY_SUBJECT_MISMATCH = 20;
const PENALTY_AGGREGATION_MISMATCH = 15;
const PENALTY_PERIOD_MISMATCH = 15;
const PENALTY_OVER_MODIFIER = 5;

/**
 * spec이 valid한지 (필수 dimension 존재 + 문자열)
 */
function _validateSpec(spec) {
  if (!spec || typeof spec !== 'object') return false;
  for (const k of DIMENSION_REQUIRED) {
    if (!spec[k] || typeof spec[k] !== 'string') return false;
  }
  return true;
}

/**
 * 단일 intention dims와 spec 매칭 결과. 후보 제외 시 null, 통과 시 score 계산.
 *
 * @param {object} dims — INTENTION_DIMENSIONS entry
 * @param {DimensionSpec} spec
 * @returns {number|null} score 또는 null (후보 제외)
 */
function _matchOne(dims, spec) {
  // 1. category 필수 일치
  if (dims.category !== spec.category) return null;
  // 2. action 필수 일치
  if (dims.action !== spec.action) return null;

  // 3. modifier preservation — user ⊆ catalog (B4-1)
  const userMods = Array.isArray(spec.modifier) ? spec.modifier : [];
  const catalogMods = Array.isArray(dims.modifier) ? dims.modifier : [];
  for (const m of userMods) {
    if (!catalogMods.includes(m)) return null;
  }

  // 4. score 계산
  let score = SCORE_BASE;

  if (spec.subject != null) {
    if (dims.subject !== spec.subject) score -= PENALTY_SUBJECT_MISMATCH;
  }
  if (spec.aggregation != null) {
    if (dims.aggregation !== spec.aggregation) score -= PENALTY_AGGREGATION_MISMATCH;
  }
  if (spec.period != null) {
    if (dims.period !== spec.period) score -= PENALTY_PERIOD_MISMATCH;
  }

  // over-modifier — catalog가 user보다 많은 modifier 보유
  const extraMods = catalogMods.length - userMods.length;
  if (extraMods > 0) score -= extraMods * PENALTY_OVER_MODIFIER;

  return score;
}

/**
 * Best match intention 1건 반환. 후보 0건이면 null.
 *
 * @param {DimensionSpec} spec
 * @returns {LookupResult|null}
 */
export function findIntentionByDimensions(spec) {
  if (!_validateSpec(spec)) return null;

  let best = null;
  for (const [key, dims] of Object.entries(INTENTION_DIMENSIONS)) {
    const score = _matchOne(dims, spec);
    if (score == null) continue;
    if (best == null || score > best.score) {
      best = { key, dims, score };
    }
    // tie 시 첫 발견 유지 (score === best.score, 갱신 안 함)
  }
  return best;
}

/**
 * Top-K candidates 반환 (디버깅 / fallback 후보 제시 용).
 * score desc, tie 시 등록 순서.
 *
 * @param {DimensionSpec} spec
 * @param {{topK?: number}} [opts]
 * @returns {LookupResult[]}
 */
export function findIntentionsByDimensions(spec, { topK = 5 } = {}) {
  if (!_validateSpec(spec)) return [];

  const candidates = [];
  for (const [key, dims] of Object.entries(INTENTION_DIMENSIONS)) {
    const score = _matchOne(dims, spec);
    if (score == null) continue;
    candidates.push({ key, dims, score });
  }
  // sort stable — score desc. Object.entries 순서로 tie-break (등록 순서).
  candidates.sort((a, b) => b.score - a.score);
  return candidates.slice(0, Math.max(0, topK));
}

/**
 * RL-1 Detection.forceDimensions를 직접 forward하는 헬퍼.
 * forceDimensions가 null/undefined 시 null 반환 (caller가 fallback 처리).
 *
 * @param {object|null|undefined} forceDimensions — RL-1 detection.forceDimensions
 * @returns {LookupResult|null}
 */
export function resolveFromForceDimensions(forceDimensions) {
  if (!forceDimensions || typeof forceDimensions !== 'object') return null;
  return findIntentionByDimensions(forceDimensions);
}
